import http from '@/api/client'
import type { ApiResponse, PageResponse } from '@/types/api'
import type {
  CourseDetail,
  CourseLevel,
  CourseOrder,
  CoursePaymentMethod,
  CourseReview,
  CourseSummary,
  LearnLesson,
  MyCourse,
  MyCourseDetailVO,
  ProgressSubmitRequest,
  ReviewSubmitRequest,
} from '@/types/course'

/* ============================================================
 * 课程相关接口
 *   - 列表 / 详情为 C 端匿名可看
 *   - 我的课程 / 课时 / 进度 / 评价 / 订单 需要登录
 *
 * 接口路径对齐后端 §3.5：
 *   POST /app/course/{id}/order/create
 *   GET  /app/course/order/{orderNo}
 *   POST /app/course/order/{orderNo}/pay-success (dev only)
 *   GET  /app/course/my
 *   GET  /app/course/my/{id}             我的课程详情（进度 + 评价状态，2026-08-31 新增）
 *   GET  /app/course/{id}/lessons/{lessonId}
 *   POST /app/course/lesson/{id}/progress
 *   POST /app/course/review              评价提交（独立接口，2026-08-31 起替代 /app/course/{id}/review）
 * ============================================================ */

export interface CourseListParams {
  keyword?: string
  level?: CourseLevel
  sort?: 'latest' | 'hot' | 'score'
  pageNum?: number
  pageSize?: number
}

export async function getCourseList(
  params: CourseListParams = {},
): Promise<PageResponse<CourseSummary>> {
  const { data } = await http.get<PageResponse<CourseSummary>>('/app/course/list', {
    params,
  })
  return data
}

export async function getCourseDetail(id: number | string): Promise<ApiResponse<CourseDetail>> {
  const { data } = await http.get<ApiResponse<CourseDetail>>(`/app/course/${id}`)
  return data
}

export interface CourseReviewsParams {
  pageNum?: number
  pageSize?: number
}

export async function getCourseReviews(
  id: number | string,
  params: CourseReviewsParams = {},
): Promise<PageResponse<CourseReview>> {
  const { data } = await http.get<PageResponse<CourseReview>>(`/app/course/${id}/reviews`, {
    params,
  })
  return data
}

/* ============================================================
 * 订单 / 支付
 *   - createOrder 已有未过期 PAYING 会直接返回该订单（防重）
 *   - price=0 直接 ENTITLED
 *   - 多渠道（§2.8）：paymentMethod 不传后端缺省 wechat；
 *     merchantKey 不传取该渠道默认商户。前端**只透传**，不写死默认值。
 * ============================================================ */

export interface CreateOrderRequest {
  paymentMethod?: CoursePaymentMethod
  /** 商户标识；不传（或 null）= 用该渠道默认商户 */
  merchantKey?: string | null
}

export async function createCourseOrder(
  courseId: number | string,
  payload: CreateOrderRequest = {},
): Promise<ApiResponse<CourseOrder>> {
  const body: CreateOrderRequest = {}
  if (payload.paymentMethod !== undefined) body.paymentMethod = payload.paymentMethod
  if (payload.merchantKey !== undefined) body.merchantKey = payload.merchantKey

  const { data } = await http.post<ApiResponse<CourseOrder>>(
    `/app/course/${courseId}/order/create`,
    body,
  )
  return data
}

export async function getCourseOrder(orderNo: string): Promise<ApiResponse<CourseOrder>> {
  const { data } = await http.get<ApiResponse<CourseOrder>>(`/app/course/order/${orderNo}`)
  return data
}

/** dev-mock 模拟支付成功；测试服 `wxpay.dev-mock=true` 时可用，生产环境返回 404。 */
export async function mockPaySuccess(orderNo: string): Promise<ApiResponse<unknown>> {
  const { data } = await http.post<ApiResponse<unknown>>(`/app/course/order/${orderNo}/pay-success`)
  return data
}

export interface MyCourseOrdersParams {
  pageNum?: number
  pageSize?: number
}

/** 我的课程订单分页列表（后端 §2.9 `/app/course/order/list`，TableDataInfo 格式） */
export async function getMyCourseOrders(
  params: MyCourseOrdersParams = {},
): Promise<PageResponse<CourseOrder>> {
  const { data } = await http.get<PageResponse<CourseOrder>>('/app/course/order/list', {
    params,
  })
  return data
}

/* ============================================================
 * 我的课程 / 学习
 * ============================================================ */

export async function getMyCourses(): Promise<ApiResponse<MyCourse[]>> {
  const { data } = await http.get<ApiResponse<MyCourse[]>>('/app/course/my')
  return data
}

/**
 * 我的课程详情（单门）：课程信息 + 权益状态 + 学习进度 + 评价状态。
 * - 需登录 + 当前用户对该课程至少有一笔订单（无订单返 500 "课程不存在或未购买"）。
 * - 用于详情页"写评价"按钮可见性判断与"我的评价"pin 展示。
 */
export async function getMyCourseDetail(
  id: number | string,
): Promise<ApiResponse<MyCourseDetailVO>> {
  const { data } = await http.get<ApiResponse<MyCourseDetailVO>>(`/app/course/my/${id}`)
  return data
}

export async function getLesson(
  courseId: number | string,
  lessonId: number | string,
): Promise<ApiResponse<LearnLesson>> {
  const { data } = await http.get<ApiResponse<LearnLesson>>(
    `/app/course/${courseId}/lessons/${lessonId}`,
  )
  return data
}

export async function submitLessonProgress(
  lessonId: number | string,
  payload: ProgressSubmitRequest,
): Promise<ApiResponse<unknown>> {
  const { data } = await http.post<ApiResponse<unknown>>(
    `/app/course/lesson/${lessonId}/progress`,
    payload,
  )
  return data
}

/* ============================================================
 * 评价
 *   - 需 ENTITLED 权限；每课程每位用户仅一条
 *   - 2026-08-31 起改用独立接口 `POST /app/course/review`（body 传 courseId/orderId 二选一）
 *     旧 `POST /app/course/{id}/review` 已 deprecated
 *   - 前端默认传 courseId（详情页天然持有，无需 orderId）
 * ============================================================ */

export async function submitCourseReview(
  payload: ReviewSubmitRequest,
): Promise<ApiResponse<unknown>> {
  const { data } = await http.post<ApiResponse<unknown>>('/app/course/review', payload)
  return data
}
